import { Switch, Route, Redirect } from "wouter";
import { Toaster } from "./components/ui/toaster";
import { SlowRequestNotice } from "./components/feedback/SlowRequestNotice";
import { Toaster as SonnerToaster } from "sonner";
import { ProtectedRoute } from "./lib/protected-route";
import { InstructorRoute } from "./lib/admin-route";
import { DashboardRedirect } from "./lib/dashboard-redirect";
import { PanelRoute, SmartPanelRedirect } from "./lib/panel-route";
import { PanelRedirect } from "./components/guards/PanelRedirect";
import { ProfessionalProfileGuard } from "./components/feature-guards/ProfessionalProfileGuard";
import { JobAccessGuard } from "./components/feature-guards/JobAccessGuard";
import { JobOwnerGuard } from "./components/feature-guards/JobOwnerGuard";
import { JobPostingGuard } from "./components/feature-guards/JobPostingGuard";
import { CertificateAdminGuard } from "./components/feature-guards/CertificateAdminGuard";
import { SellerPanelGuard } from "./components/feature-guards/SellerPanelGuard";
import React, { Suspense, lazy, useEffect } from "react";
import { Loader2 } from "lucide-react";
import Layout from "./components/layout/layout";
import InstructorPanelLayout from "./components/layout/instructor-panel-layout";
import SellerPanelLayout from "./components/layout/seller-panel-layout";
import ProfessionalPanelLayout from "./components/layout/professional-panel-layout";
import { CartProvider } from "./hooks/use-cart";
import { PendingPaymentModal } from "./components/subscription/PendingPaymentModal";
import { usePendingPayment } from "./hooks/use-pending-payment";
import { handleError, setupGlobalErrorHandling } from "./lib/error-monitoring";
import { setupStripeErrorHandler } from "./lib/stripe-error-handler";
import { ErrorBoundary } from "./components/error-boundary";
import { ConfirmDialogProvider } from "./components/ui/confirm-dialog";
import { EmailVerificationBanner } from "./components/auth/email-verification-banner";
import { GooglePasswordSetupBanner } from "./components/auth/google-password-setup-banner";
import { useFirebaseAuth } from "./hooks/use-firebase-auth";
import CourseAuthoringLayout from "./components/layout/course-authoring-layout";
// import

// Loading fallback component
function LoadingFallback() {
  return (
    // Out of the page flow (fixed): the header appearing around it moved it
    // up and down while the page loaded (layout shift).
    <div className="fixed inset-0 flex items-center justify-center bg-dark">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

// Error fallback component for failed lazy loads

// Enhanced lazy loading with error handling
const createLazyComponent = (importFn: () => Promise<Record<string, unknown>>) => {
  return lazy(async () => {
    try {
      const componentModule = (await importFn()) as Record<string, unknown> | undefined | null;
      // Mobile Safari can RESOLVE a failed dynamic import to `undefined` (rather
      // than rejecting) — especially racing the stale-chunk reload we trigger on
      // `vite:preloadError`. Without this guard, `componentModule.default` throws
      // "undefined is not an object (evaluating 's.default')" (Sentry
      // cf03f060…) which doesn't match the chunk-failure patterns below, so it
      // dead-ends on "Page Not Found" instead of recovering. Re-throw a
      // chunk-failure message so it routes into the reload-once recovery.
      if (!componentModule || typeof componentModule !== "object") {
        throw new Error("Importing a module script failed");
      }
      const DefaultExport =
        typeof componentModule.default === "function"
          ? (componentModule.default as React.ComponentType<any>)
          : (Object.values(componentModule).find((v) => typeof v === "function") as
              | React.ComponentType<any>
              | undefined);
      if (!DefaultExport) {
        throw new Error("Lazy import has no default or single function export");
      }
      const wrapped = { default: DefaultExport };
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('drx:lazy-reload-attempted');
      }
      return wrapped;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isChunkLoadFailure =
        /Failed to fetch dynamically imported module/i.test(errorMessage) ||
        /Importing a module script failed/i.test(errorMessage) ||
        /Loading chunk [\w-]+ failed/i.test(errorMessage) ||
        /not a valid JavaScript MIME type/i.test(errorMessage) ||
        /text\/html/i.test(errorMessage);

      if (typeof window !== 'undefined' && isChunkLoadFailure) {
        const hasReloaded = sessionStorage.getItem('drx:lazy-reload-attempted') === 'true';
        if (!hasReloaded) {
          // Stale-chunk after a deploy → reload the page to pick up the new
          // index.html. Don't forward this to Sentry (was a 24-events-in-7d
          // noise source — Sentry #S #2E #P #1X) — auto-reload makes it a
          // non-event for the user.
          sessionStorage.setItem('drx:lazy-reload-attempted', 'true');
          window.location.reload();
          // Return early — do not call handleError or render the fallback.
          return new Promise(() => {}) as Promise<{ default: React.ComponentType<any> }>;
        }
        // Second attempt failed too — escalate to Sentry once.
        handleError(error, 'lazyComponentLoad');
      } else {
        handleError(error, 'lazyComponentLoad');
      }

      // Return a fallback component instead of throwing
      return {
        default: () => (
          <div className="container mx-auto p-6">
            <div className="text-center text-red-500">
              <h2 className="text-xl font-semibold mb-2">Page Not Found</h2>
              <p>The requested page could not be loaded.</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-primary text-white rounded"
              >
                Refresh Page
              </button>
            </div>
          </div>
        ),
      };
    }
  });
};

// Lazy load pages for better performance
const HomePage = createLazyComponent(() => import("@/pages/public/home-page"));
const AboutPage = createLazyComponent(() => import("@/pages/public/about-page"));
const SupportPage = createLazyComponent(() => import("@/pages/public/support-page"));
const PrivacyPolicyPage = createLazyComponent(() => import("@/pages/public/privacy-policy"));
const TermsOfServicePage = createLazyComponent(() => import("@/pages/public/terms-of-service"));
const RefundPolicyPage = createLazyComponent(() => import("@/pages/public/refund-policy"));
const AuthPage = createLazyComponent(() => import("@/pages/auth/auth-page"));
const RegistrationFlowPage = createLazyComponent(() => import("@/pages/registration-flow"));
const VerifyEmailPage = createLazyComponent(() => import("@/pages/auth/verify-email"));
// Course Certification Module
const CoursesPage = createLazyComponent(() => import("@/pages/courses/courses-page"));
const CourseDetailsPage = createLazyComponent(
  () => import("@/pages/courses/course-details-page")
);
const MyCertificationsPage = createLazyComponent(
  () => import("@/pages/courses/my-certifications-page")
);

const MyCoursesPage = createLazyComponent(() => import("@/pages/courses/my-courses-page"));
const CourseModulePage = createLazyComponent(
  () => import("@/pages/courses/course-module-page")
);
const LessonPage = createLazyComponent(() => import("@/pages/courses/lesson-page"));
const QuizPage = createLazyComponent(() => import("@/pages/courses/quiz-page"));
const CertificatePage = createLazyComponent(() => import("@/pages/courses/certificate-page"));
const ClassPage = createLazyComponent(() => import("@/pages/courses/class-page"));
const CourseAdminDashboardPage = createLazyComponent(
  () => import("@/pages/courses/course-admin-dashboard")
);
const GuidelinesPage = createLazyComponent(() => import("@/pages/public/guidelines-page"));
const CopyrightPolicyPage = createLazyComponent(() => import("@/pages/public/copyright-policy-page"));
const MembershipComparisonPage = createLazyComponent(() => import("@/pages/public/membership-comparison-page"));
const CourseViewPage = createLazyComponent(() => import("@/pages/courses/course-view"));
const LessonViewPage = createLazyComponent(() => import("@/pages/courses/lesson-view"));
const QuizViewPage = createLazyComponent(() => import("@/pages/courses/quiz-view"));
const ExamTakePage = createLazyComponent(() => import("@/pages/courses/exam-take-page"));
const CertificateAdminPage = createLazyComponent(() => import("@/pages/instructor-panel/certificates"));
const AssessmentsPage = createLazyComponent(() => import("@/pages/instructor-panel/assessments"));
const VerifyCertificatePage = createLazyComponent(() => import("@/pages/courses/verify-certificate-page"));

// Connect Module
const ConnectPage = createLazyComponent(() => import("@/pages/connect/connect-page"));
const ConnectSearchPage = createLazyComponent(() => import("@/pages/connect/connect-search-page"));
const InstructorProfilePage = createLazyComponent(
  () => import("@/pages/connect/instructor-profile-page")
);

// Curriculum Resource Module
const CurriculumPage = createLazyComponent(() => import("@/pages/curriculum/curriculum-page-proper"));
const ResourceDetailsPage = createLazyComponent(
  () => import("@/pages/curriculum/curriculum-details-page")
);
const MyResourcesPage = createLazyComponent(() => import("@/pages/curriculum/my-resources-page"));
const SellerStorePage = createLazyComponent(() => import("@/pages/curriculum/seller-store-page"));
const EarningsPage = createLazyComponent(() => import("@/pages/earnings/earnings-page"));
const StorefrontPage = createLazyComponent(() => import("@/pages/curriculum/storefront-page"));

// Subscription Module
const SubscriptionPage = createLazyComponent(
  () => import("./pages/subscription/subscription-page")
);
const SubscriptionSuccessPage = createLazyComponent(
  () => import("./pages/subscription/subscription-success-page")
);
const SubscriptionUpgradePage = createLazyComponent(
  () => import("./pages/subscription/subscription-upgrade")
);
const SubscriptionUpgradeSuccessPage = createLazyComponent(
  () => import("./pages/subscription/subscription-upgrade-success")
);
const SubscriptionUpgradeCancelPage = createLazyComponent(
  () => import("./pages/subscription/subscription-upgrade-cancel")
);

// Jobs Module
const MyJobsPage = createLazyComponent(() => import("./pages/jobs/my-jobs"));
const JobsSearchPage = createLazyComponent(() => import("./pages/jobs/jobs-search"));
const CreateJobPage = createLazyComponent(() => import("./pages/jobs/create"));
const JobSuccessPage = createLazyComponent(() => import("./pages/jobs/create/success"));
const JobApplicantsPage = createLazyComponent(() => import("./pages/jobs/[id]/applicants"));
const JobEditPage = createLazyComponent(() => import("./pages/jobs/[id]/edit"));
const JobViewPage = createLazyComponent(() => import("./pages/jobs/[id]/view"));
const PublicJobViewPage = createLazyComponent(() => import("./pages/jobs/[id]/public-view"));
const NotificationsPage = createLazyComponent(() => import("./pages/dashboard/notifications"));

// Messages Module
const MessagesPage = createLazyComponent(() => import("./pages/dashboard/messages"));
// Temporarily bypass lazy loading for debugging
import JobManagePageDirect from "./pages/jobs/[id]/manage";
const JobManagePage = () => <JobManagePageDirect />;

// Shopping Cart and Checkout Module
const CartPage = createLazyComponent(() => import("./pages/checkout/cart-page"));
const CheckoutPage = createLazyComponent(
  () => import("./pages/checkout/checkout-page")
);
const CheckoutCompletePage = createLazyComponent(
  () => import("./pages/checkout/checkout-page-complete")
);
const CheckoutSuccessPage = createLazyComponent(
  () => import("./pages/checkout/checkout-success")
);
const StripeCheckoutPage = createLazyComponent(
  () => import("./pages/checkout/stripe")
);
const SimpleCheckoutPage = createLazyComponent(
  () => import("./pages/checkout/simple-checkout")
);
const PaymentSuccessPage = createLazyComponent(
  () => import("./pages/checkout/payment-success")
);
const CancelPaymentPage = createLazyComponent(
  () => import("./pages/checkout/cancel-payment")
);
const MyPurchasesPage = createLazyComponent(
  () => import("./pages/checkout/my-purchases")
);
const OrderHistoryPage = createLazyComponent(
  () => import("./pages/checkout/order-history-page")
);

// Dashboard Pages
const DashboardPage = createLazyComponent(
  () => import("./pages/dashboard/dashboard-page")
);
const ProfilePage = createLazyComponent(() => import("./pages/dashboard/profile"));
const FavoritesPage = createLazyComponent(
  () => import("./pages/dashboard/favorites-page")
);
const UserProfilePage = createLazyComponent(
  () => import("./pages/dashboard/user-profile-page")
);

// Course edit component (still used)
const CourseEditPages = createLazyComponent(
  () => import("./components/form/Edit-course")
);


// === NEW ENHANCED COMPONENTS ===
const ExamBuilderComponent = createLazyComponent(
  () => import("./components/exams/ExamBuilder")
);
const ConnectWizardComponent = createLazyComponent(
  () => import("./components/connect/connect-wizard")
);

const ConnectWizardPage = () => {
  const { user } = useFirebaseAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ConnectWizardComponent
        mode="get-booked"
        onComplete={() => {}}
        user={user}
      />
    </Suspense>
  );
};

const LegacyMultiDashboardRedirect = () => <Redirect to="/dashboard" />;

// Each layout wrapper is made once per page and reused. Router calls these
// as it renders, and it re-renders whenever AppWithModals does (the pending-
// payment check updates state every 30 s). A fresh wrapper each time is a new
// component type, so React unmounted and remounted the open page: a learner's
// quiz result went back to the quiz intro, half-typed forms emptied, dialogs shut.
function onceEach<C extends object, R>(make: (component: C) => R): (component: C) => R {
  const made = new WeakMap<C, R>();
  return (component) => {
    if (!made.has(component)) made.set(component, make(component));
    return made.get(component)!;
  };
}

const withCourseAuthoringLayout = onceEach((Component: React.ComponentType) => {
  const WrappedComponent = () => (
    <CourseAuthoringLayout>
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      </ErrorBoundary>
    </CourseAuthoringLayout>
  );
  WrappedComponent.displayName = `withCourseAuthoringLayout(${Component.displayName || Component.name})`;
  return WrappedComponent;
});

// Layout wrapper functions
const withLayout = onceEach((Component: React.ComponentType) => {
  const WrappedComponent = () => (
    <Layout>
      {/* Per-route ErrorBoundary: a render throw in one page shows the boundary
          fallback (with retry + Sentry logging) instead of blanking the whole
          SPA. */}
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      </ErrorBoundary>
    </Layout>
  );
  WrappedComponent.displayName = `withLayout(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
});

const withInstructorLayout = onceEach((Component: React.ComponentType<any>) => {
  const WrappedComponent = () => {
    const [activeTab, setActiveTab] = React.useState('dashboard');

    return (
      <InstructorPanelLayout activeTab={activeTab} setActiveTab={setActiveTab}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Component activeTab={activeTab} setActiveTab={setActiveTab} />
          </Suspense>
        </ErrorBoundary>
      </InstructorPanelLayout>
    );
  };
  WrappedComponent.displayName = `withInstructorLayout(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
});

const withSellerLayout = onceEach((Component: React.ComponentType<any>) => {
  const WrappedComponent = () => {
    const [activeTab, setActiveTab] = React.useState('resources');

    return (
      <SellerPanelLayout activeTab={activeTab} onTabChange={setActiveTab}>
        <ErrorBoundary>
          <Suspense fallback={<LoadingFallback />}>
            <Component activeTab={activeTab} setActiveTab={setActiveTab} />
          </Suspense>
        </ErrorBoundary>
      </SellerPanelLayout>
    );
  };
  WrappedComponent.displayName = `withSellerLayout(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
});

const withProfessionalLayout = onceEach((Component: React.ComponentType<any>) => {
  const WrappedComponent = () => (
    <ProfessionalPanelLayout>
      <ErrorBoundary>
        <Suspense fallback={<LoadingFallback />}>
          <Component />
        </Suspense>
      </ErrorBoundary>
    </ProfessionalPanelLayout>
  );
  WrappedComponent.displayName = `withProfessionalLayout(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
});

// Individual Instructor Page Components
// Individual Instructor Page Components (like admin panel)
const InstructorDashboardPage = createLazyComponent(() => import("@/pages/instructor-panel/dashboard"));
const InstructorCoursesPage = createLazyComponent(() => import("@/pages/instructor-panel/courses"));
const MyClassPage = createLazyComponent(() => import("@/pages/instructor-panel/my-class"));
const CourseMaterialsPage = createLazyComponent(() => import("@/pages/instructor-panel/course-materials"));
const InstructorEnrollmentsPage = createLazyComponent(() => import("@/pages/instructor-panel/enrollments"));
const InstructorSettingsPage = createLazyComponent(() => import("@/pages/instructor-panel/settings"));
const ProfessionalDashboardPage = createLazyComponent(() => import("@/pages/professional-panel/dashboard"));
const ProfessionalProfileHubPage = createLazyComponent(() => import("@/pages/professional-panel/profile"));
const ProfessionalJobsPage = createLazyComponent(() => import("@/pages/professional-panel/jobs"));
const ProfessionalSettingsPage = createLazyComponent(() => import("@/pages/professional-panel/settings"));

// Individual Seller Panel Pages (like instructor panel)
const SellerDashboardPage = createLazyComponent(() => import("@/pages/seller-panel/dashboard"));
const SellerResourcesPage = createLazyComponent(() => import("@/pages/seller-panel/resources"));
const SellerCoursesPage = createLazyComponent(() => import("@/pages/seller-panel/courses"));
const SellerAnalyticsPage = createLazyComponent(() => import("@/pages/seller-panel/analytics"));
const SellerSettingsPage = createLazyComponent(() => import("@/pages/seller-panel/settings"));
const SellerStorefrontPage = createLazyComponent(() => import("@/pages/seller-panel/storefront"));
// 404 Not Found Page
const NotFoundPage = createLazyComponent(() => import("@/pages/public/404"));

// Page components with layouts applied
const Pages = {
  // Public Pages
  Home: withLayout(HomePage),
  About: withLayout(AboutPage),
  Support: withLayout(SupportPage),
  PrivacyPolicy: withLayout(PrivacyPolicyPage),
  TermsOfService: withLayout(TermsOfServicePage),
  Auth: withLayout(AuthPage),
  RegistrationFlow: withLayout(RegistrationFlowPage),
  NotFound: withLayout(NotFoundPage),

  // Policy Pages
  Guidelines: withLayout(GuidelinesPage),
  CopyrightPolicy: withLayout(CopyrightPolicyPage),
  RefundPolicy: withLayout(RefundPolicyPage),
  MembershipComparison: withLayout(MembershipComparisonPage),

  // Course Certification Module
  Courses: withLayout(CoursesPage),
  CourseDetails: withLayout(CourseDetailsPage),
  MyCertifications: withLayout(MyCertificationsPage),
  MyCourses: withLayout(MyCoursesPage),
  CourseModule: withLayout(CourseModulePage),
  Lesson: withLayout(LessonPage),
  Quiz: withLayout(QuizPage),
  Certificate: withLayout(CertificatePage),
  /** Certificate admin (ADMIN | INSTRUCTOR): issued certificates, issue / revoke, enroll */
  // In the Instructor panel like the rest of its menu (they opened outside it).
  InstructorCertificates: withInstructorLayout(CertificateAdminPage),
  InstructorAssessments: withInstructorLayout(AssessmentsPage),

  // Connect Module
  Connect: withLayout(ConnectPage),
  ConnectSearch: withLayout(ConnectSearchPage),
  InstructorProfile: withLayout(InstructorProfilePage),
  getBooked: withLayout(ConnectWizardPage),

  // Curriculum Resource Module
  Curriculum: withLayout(CurriculumPage),
  // CurriculumCombined: withLayout(CurriculumPageCombined),
  ResourceDetails: withLayout(ResourceDetailsPage),
  MyResources: withLayout(MyResourcesPage),
  SellerStore: withLayout(SellerStorePage),
  Storefront: StorefrontPage,

  // Subscription Module
  Subscription: withLayout(SubscriptionPage),
  SubscriptionSuccess: withLayout(SubscriptionSuccessPage),
  SubscriptionUpgrade: withLayout(SubscriptionUpgradePage),
  SubscriptionUpgradeSuccess: withLayout(SubscriptionUpgradeSuccessPage),
  SubscriptionUpgradeCancel: withLayout(SubscriptionUpgradeCancelPage),

  // Jobs Module
  MyJobs: withLayout(MyJobsPage),
  JobsSearch: withLayout(JobsSearchPage),
  CreateJob: withLayout(CreateJobPage),
  JobSuccess: withLayout(JobSuccessPage),
  JobApplicants: withLayout(JobApplicantsPage),
  JobEdit: withLayout(JobEditPage),
  JobManage: withLayout(JobManagePage),
  JobView: withLayout(JobViewPage),
  PublicJobView: withLayout(PublicJobViewPage),
  Notifications: withLayout(NotificationsPage),
  // Messages Module
  Messages: withLayout(MessagesPage),

  // Shopping Cart and Checkout Module
  Cart: withLayout(CartPage),
  Checkout: withLayout(CheckoutPage),
  CheckoutComplete: withLayout(CheckoutCompletePage),
  CheckoutSuccess: withLayout(CheckoutSuccessPage),
  StripeCheckout: withLayout(StripeCheckoutPage),
  SimpleCheckout: withLayout(SimpleCheckoutPage),
  PaymentSuccess: withLayout(PaymentSuccessPage),
  CancelPayment: withLayout(CancelPaymentPage),
  MyPurchases: withLayout(MyPurchasesPage),
  OrderHistory: withLayout(OrderHistoryPage),

  // Dashboard Pages
  Dashboard: withLayout(DashboardPage),
  Profile: withLayout(ProfilePage),
  Favorites: withLayout(FavoritesPage),
  UserProfile: withLayout(UserProfilePage),

  // Instructor Pages
  CourseEdit: withLayout(CourseEditPages),
  CourseCreate: withLayout(CourseAdminDashboardPage),
  CourseDetail: withLayout(CourseAdminDashboardPage),
  // Keep authors in the panel available to their membership.
  InstructorCourseCreate: withCourseAuthoringLayout(CourseAdminDashboardPage),
  InstructorCourseDetail: withCourseAuthoringLayout(CourseAdminDashboardPage),
  InstructorCourseEdit: withCourseAuthoringLayout(CourseEditPages),
  InstructorStudents: withInstructorLayout(InstructorEnrollmentsPage),
  /**
   * The Instructor Panel's Quizzes tab. Was QuizPage — the quiz-TAKING screen,
   * which reads useParams().quizId. /instructor/quizzes carries no :quizId, so
   * both of its queries stayed disabled and the tab rendered nothing. Quiz and
   * question authoring actually lives in the course admin dashboard
   * (createQuiz / addQuizQuestion), so point the tab there.
   */
  AdminQuizzes: withInstructorLayout(CourseAdminDashboardPage),
  InstructorCourses: withLayout(CoursesPage),

  // Individual Instructor Pages (like admin panel)
  InstructorDashboard: withInstructorLayout(InstructorDashboardPage),
  InstructorPanelCourses: withInstructorLayout(InstructorCoursesPage),
  InstructorMyClass: withInstructorLayout(MyClassPage),
  InstructorCourseMaterials: withCourseAuthoringLayout(CourseMaterialsPage),
  InstructorEnrollments: withInstructorLayout(InstructorEnrollmentsPage),
  InstructorSettings: withInstructorLayout(InstructorSettingsPage),

  ProfessionalDashboard: withProfessionalLayout(ProfessionalDashboardPage),
  ProfessionalProfileHub: withProfessionalLayout(ProfessionalProfileHubPage),
  ProfessionalJobs: withProfessionalLayout(ProfessionalJobsPage),
  ProfessionalSettings: withProfessionalLayout(ProfessionalSettingsPage),

  // Individual Seller Pages (like instructor panel)
  SellerDashboard: withSellerLayout(SellerDashboardPage),
  SellerResources: withSellerLayout(SellerResourcesPage),
  SellerCourses: withSellerLayout(SellerCoursesPage),
  SellerAnalytics: withSellerLayout(SellerAnalyticsPage),
  SellerSettings: withSellerLayout(SellerSettingsPage),
  SellerStorefront: withSellerLayout(SellerStorefrontPage),
  SellerPayments: withSellerLayout(() => <EarningsPage panel="seller" />),
  InstructorEarnings: withInstructorLayout(() => <EarningsPage panel="instructor" />),

  // In the Instructor panel like its other tabs; it opened without the sidebar.
  ExamBuilder: withInstructorLayout(ExamBuilderComponent),
};

// Guarded route wrappers are hoisted to module scope so their component identity
// stays stable across Router re-renders. Declaring them inside Router() made wouter
// receive a new `component` prop on every auth/subscription update, which React
// treated as a new component type — unmounting and remounting the entire page
// subtree (wizard/job forms), wiping form state, refs, and effect guards. That
// looked like "flicker" on the /connect/get-booked and /jobs/:id/edit flows.
const GuardedGetBookedPage = () => (
  <ProfessionalProfileGuard>
    <Pages.getBooked />
  </ProfessionalProfileGuard>
);

// Job posting requires Royalty+ (CONTACT_BOOK_PROFESSIONALS feature). Without
// this guard, a Nobility user could navigate to /jobs/create, fill the entire
// 6-step wizard, and only learn at submit time that the service rejects their
// tier. JobPostingGuard short-circuits to the upgrade modal up front.
const GuardedCreateJobPage = () => (
  <JobPostingGuard>
    <Pages.CreateJob />
  </JobPostingGuard>
);

// Strict tier gate: a user who lapses must lose edit/manage access immediately,
// even on jobs they previously created. Their job documents in Firestore are
// untouched — only the management UI is gated.
const GuardedMyJobsPage = () => (
  <JobAccessGuard authReturnTo="/my-jobs">
    <Pages.MyJobs />
  </JobAccessGuard>
);
const GuardedJobApplicantsPage = () => (
  <JobAccessGuard authReturnTo="/my-jobs">
    <JobOwnerGuard>
      <Pages.JobApplicants />
    </JobOwnerGuard>
  </JobAccessGuard>
);
const GuardedJobEditPage = () => (
  <JobAccessGuard authReturnTo="/my-jobs">
    <JobOwnerGuard>
      <Pages.JobEdit />
    </JobOwnerGuard>
  </JobAccessGuard>
);
const GuardedJobManagePage = () => (
  <JobAccessGuard authReturnTo="/my-jobs">
    <JobOwnerGuard>
      <Pages.JobManage />
    </JobOwnerGuard>
  </JobAccessGuard>
);


const GuardedInstructorCertificates = () => (
  <CertificateAdminGuard>
    <Pages.InstructorCertificates />
  </CertificateAdminGuard>
);

const GuardedInstructorAssessments = () => (
  <CertificateAdminGuard>
    <Pages.InstructorAssessments />
  </CertificateAdminGuard>
);
function EarningsRedirect({ tab, extra }: { tab: string; extra?: string }) {
  const q = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
  if (!q.has("tab") && !q.has("stripeReturn")) q.set("tab", tab);
  if (extra) for (const [k, v] of new URLSearchParams(extra)) if (!q.has(k)) q.set(k, v);
  return <Redirect to={`/instructor/earnings?${q.toString()}`} replace />;
}
/** Essay review became Assessments; old links (and ?course=) still land there. */
const EssaysToAssessments = () => (
  <Redirect to={`/instructor/assessments${typeof window !== "undefined" ? window.location.search : ""}`} replace />
);

const GuardedSellerStorePage = () => (
  <SellerPanelGuard>
    <Pages.SellerStore />
  </SellerPanelGuard>
);

const GuardedLegacySellerDashboardPage = () => (
  <SellerPanelGuard>
    <Pages.SellerDashboard />
  </SellerPanelGuard>
);

function Router() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Switch>
        {/* Public Routes */}
        <Route path="/" component={Pages.Home} />
        <Route path="/about" component={Pages.About} />
        <Route path="/support" component={Pages.Support} />
        <Route path="/privacy-policy" component={Pages.PrivacyPolicy} />
        <Route path="/terms-of-service" component={Pages.TermsOfService} />
        <Route path="/auth" component={Pages.Auth} />
        {/* Legacy duplicate auth page retired — canonical auth lives at /auth. */}
        <Route path="/firebase-auth">
          <Redirect to="/auth" replace />
        </Route>
        <Route path="/register" component={Pages.RegistrationFlow} />
        <Route path="/verify-email" component={withLayout(VerifyEmailPage)} />
        {/* Public face of an instructor's "My Class" page. */}
        <Route path="/class/:instructorId" component={withLayout(ClassPage)} />

        {/* Course Certification Module */}
        <Route path="/courses" component={Pages.Courses} />
        <Route path="/courses/:id" component={Pages.CourseDetails} />
        <ProtectedRoute path="/courses/:courseId/view" component={withLayout(CourseViewPage)} />
        <ProtectedRoute path="/courses/:courseId/modules/:moduleId/lessons/:lessonId" component={withLayout(LessonViewPage)} />
        <ProtectedRoute path="/courses/:courseId/modules/:moduleId/quizzes/:quizId" component={withLayout(QuizViewPage)} />
        <ProtectedRoute path="/courses/:courseId/exams/:examId" component={withLayout(ExamTakePage)} />
        {/* Public certificate verification -- no sign-in, shows name/course/date/status only */}
        <Route path="/verify/:code" component={withLayout(VerifyCertificatePage)} />
        <Route path="/verify" component={withLayout(VerifyCertificatePage)} />
        {/* Nothing links here any more and it rendered a blank page; the
            course page has the Enroll button. */}
        <Route path="/courses/:courseId/enroll">
          {(params: { courseId: string }) => <Redirect to={`/courses/${params.courseId}`} />}
        </Route>
        {/* One learning UI: /learn was a second one with its own progress
            logic (and hung on "Loading course..."). */}
        <Route path="/courses/:courseId/learn">
          {(params) => <Redirect to={`/courses/${params.courseId}/view`} />}
        </Route>

        {/* Redirect singular /course/:id to plural /courses/:id */}
        <Route path="/course/:id">
          {({ id }) => <Redirect to={`/courses/${id}`} replace />}
        </Route>

        <ProtectedRoute
          path="/courses/:courseId/modules/:moduleId"
          component={Pages.CourseModule}
        />
        {/* The /courses/:courseId/modules/:moduleId/lessons/:lessonId and
            .../quizzes/:quizId paths are already served by LessonViewPage /
            QuizViewPage registered above (first match wins in wouter). The
            duplicate Pages.Lesson / Pages.Quiz registrations that used to sit
            here were unreachable and have been removed. */}
        <ProtectedRoute path="/lesson/:courseId" component={Pages.Lesson} />
        <ProtectedRoute
          path="/my-certifications"
          component={Pages.MyCertifications}
        />
        <ProtectedRoute path="/my-courses" component={Pages.MyCourses} />
        {/* Templates moved into Instructor panel -> Certificates -> Templates. */}
        <Route path="/certificate-templates">
          <Redirect to="/instructor/certificates?tab=templates" replace />
        </Route>
        <ProtectedRoute
          path="/certificates"
          component={Pages.MyCertifications}
        />
        <ProtectedRoute
          path="/certificates/:certificateId"
          component={Pages.Certificate}
        />

        {/* Connect Module */}
        <Route path="/connect" component={Pages.Connect} />
        {/* Legacy /connect/search → canonical /professionals. Kept as a
            redirect so old in-app links and external bookmarks still land. */}
        <Route path="/connect/search">
          <Redirect to="/professionals" />
        </Route>
        <ProtectedRoute
          path="/connect/profile"
          component={Pages.InstructorProfile}
        />

        <ProtectedRoute
          path="/connect/get-booked"
          component={GuardedGetBookedPage}
        />


        <Route
          path="/instructors/:instructorId"
          component={Pages.InstructorProfile}
        />

        {/* Curriculum Resource Module */}

        <Route path="/curriculum" component={Pages.Curriculum} />
        <Route path="/certificate-templates/:id">
          <Redirect to="/instructor/certificates?tab=templates" replace />
        </Route>

        {/* Policy Pages */}
        <Route path="/guidelines" component={Pages.Guidelines} />
        <Route path="/copyright-policy" component={Pages.CopyrightPolicy} />
        <Route path="/refund-policy" component={Pages.RefundPolicy} />
        <Route path="/membership-comparison" component={Pages.MembershipComparison} />

        {/* Fallback to 404 */}
        {/* Before /curriculum/:id, which would read "upload" as a resource id. */}
        <Route path="/curriculum/upload">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>
        {/* withLayout: this route used the bare page, so resource pages -- the
            ones sellers share -- had no navbar, cart or footer. */}
        <Route path="/curriculum/:id" component={Pages.ResourceDetails} />
        <Route path="/sellers/:sellerId" component={Pages.SellerStore} />
        <Route
          path="/seller-store/:sellerId"
          component={Pages.SellerStore}
        />
        {/* Public Store Access */}
        <Route path="/p/storefront/:userId" component={Pages.Storefront} />
        <ProtectedRoute path="/my-store" component={GuardedSellerStorePage} />
        <ProtectedRoute path="/my-resources" component={Pages.MyResources} />
        {/* OLD SELLER ROUTES REMOVED - Now using PanelRoute below */}
        {/* Resource creation/upload only happens from My Storefront now — these
            legacy paths redirect there instead of 404ing on old links. */}
        <Route path="/seller/resources/add">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>
        <Route path="/seller/upload-resource">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>
        <Route path="/upload-resource">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>
        <Route path="/simple-upload">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>
        {/* Editing happens in My Storefront too: this old editor uploaded
            covers without the cropper. */}
        <Route path="/curriculum/:id/edit">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>

        {/* Legacy routes for backward compatibility - redirect to /curriculum */}
        <Route path="/resources">
          <Redirect to="/curriculum" replace />
        </Route>
        <Route path="/resources/:resourceId">
          {({ resourceId }) => <Redirect to={`/curriculum/${resourceId}`} replace />}
        </Route>
        <Route path="/resources/:id/edit">
          {({ id }) => <Redirect to={`/curriculum/${id}/edit`} replace />}
        </Route>

        {/* Subscription Module */}
        <Route path="/subscription" component={Pages.Subscription} />
        {/* Redirect old subscription manage URL to profile */}
        <Route path="/subscription/manage">
          <Redirect to="/profile" replace />
        </Route>
        <Route
          path="/subscription/success"
          component={Pages.SubscriptionSuccess}
        />
        <Route
          path="/subscription/upgrade"
          component={Pages.SubscriptionUpgrade}
        />
        <Route
          path="/subscription/upgrade/success"
          component={Pages.SubscriptionUpgradeSuccess}
        />
        <Route
          path="/subscription/upgrade/cancel"
          component={Pages.SubscriptionUpgradeCancel}
        />

        {/* Redirect /pricing to /subscription */}
        <Route path="/pricing">
          <Redirect to="/subscription" replace />
        </Route>

        {/* Shopping Cart and Checkout Module */}
        <Route path="/cart" component={Pages.Cart} />
        <Route path="/cart-new">
          <Redirect to="/cart" replace />
        </Route>

        <ProtectedRoute path="/checkout" component={Pages.Checkout} />
        <ProtectedRoute
          path="/checkout-complete"
          component={Pages.CheckoutComplete}
        />
        <ProtectedRoute
          path="/checkout/success"
          component={Pages.CheckoutSuccess}
        />
        <Route path="/checkout/stripe" component={Pages.StripeCheckout} />
        <Route path="/simple-checkout" component={Pages.SimpleCheckout} />
        <Route path="/payment-success" component={Pages.PaymentSuccess} />
        <Route path="/cancel-payment" component={Pages.CancelPayment} />
        <ProtectedRoute path="/my-purchases" component={Pages.MyPurchases} />

        {/* Jobs Module */}
        <ProtectedRoute path="/my-jobs" component={GuardedMyJobsPage} />
        <Route path="/jobs" component={Pages.JobsSearch} />
        <ProtectedRoute path="/jobs/create" component={GuardedCreateJobPage} />
        <ProtectedRoute path="/jobs/create/success" component={Pages.JobSuccess} />
        <ProtectedRoute path="/jobs/:id/applicants" component={GuardedJobApplicantsPage} />
        <ProtectedRoute path="/jobs/:id/edit" component={GuardedJobEditPage} />
        <ProtectedRoute path="/jobs/:id/manage" component={GuardedJobManagePage} />
        <ProtectedRoute path="/jobs/:id/view" component={Pages.JobView} />
        {/* Public job view - accessible to everyone */}
        <Route path="/jobs/:id/public" component={Pages.PublicJobView} />
        <ProtectedRoute path="/notifications" component={Pages.Notifications} />
        {/* Messages Module */}
        <ProtectedRoute path="/messages" component={Pages.Messages} />

        {/* Main dashboard entry route */}
        <Route path="/my-dashboard" component={DashboardRedirect} />
        <ProtectedRoute path="/dashboard" component={Pages.Dashboard} />

        {/* Role-specific dashboard routes */}
        <ProtectedRoute
          path="/multi-dashboard"
          component={LegacyMultiDashboardRedirect}
        />
        <ProtectedRoute path="/dashboard/user" component={Pages.Dashboard} />
        <ProtectedRoute
          path="/seller-dashboard"
          component={GuardedLegacySellerDashboardPage}
        />
        {/* Removed duplicate /seller/resources route - now handled by PanelRoute below */}
        <Route path="/seller/resources/create">
          <Redirect to="/dashboard?tab=store" replace />
        </Route>
        <ProtectedRoute path="/profile" component={Pages.Profile} />
        <ProtectedRoute path="/favorites" component={Pages.Favorites} />
        {/* /profile is the canonical profile page with inline edit.
            Old/legacy variants redirect here so external bookmarks still land. */}
        <Route path="/profile/edit">
          <Redirect to="/profile" />
        </Route>
        <Route path="/profile/manage">
          <Redirect to="/profile" />
        </Route>
        <ProtectedRoute path="/orders" component={Pages.OrderHistory} />
        <Route path="/profile/:userId" component={Pages.UserProfile} />

        {/* Instructor Routes */}
        {/* NOTE: /instructor/dashboard is intentionally NOT defined here.
            The first matching <Route> wins in wouter's <Switch>, and an
            unguarded ProtectedRoute here previously shadowed the guarded
            PanelRoute below (line ~894), letting any authenticated user reach
            the instructor dashboard without its role. The canonical,
            role-guarded definition now lives in the INSTRUCTOR PANEL block. */}
        <PanelRoute path="/instructor/courses" panelType="instructor" component={Pages.InstructorPanelCourses} />
        <PanelRoute path="/instructor/class" panelType="instructor" component={Pages.InstructorMyClass} />
        <PanelRoute path="/instructor/courses/create" panelType="course-authoring" component={Pages.InstructorCourseCreate} />
        <PanelRoute path="/instructor/courses/:id/materials" panelType="course-authoring" component={Pages.InstructorCourseMaterials} />
        {/* One course list (My Courses). This page was a second one -- a card
            catalogue with its own Manage/Edit/Delete -- so a course link
            opens its editor instead. */}
        <Route path="/instructor/courses/:id">
          {({ id }) => <Redirect to={`/instructor/courses/${id}/edit`} replace />}
        </Route>

        <PanelRoute path="/instructor/courses/:id/edit" panelType="course-authoring" component={Pages.InstructorCourseEdit} />
        <InstructorRoute
          path="/instructor/students"
          component={Pages.InstructorStudents}
        />
        {/* Quizzes are written per course (course editor -> Quiz Management);
            this used to open the course catalogue with no quiz list. */}
        <Route path="/instructor/quizzes">
          <Redirect to="/instructor/courses" />
        </Route>
        <ProtectedRoute
          path="/instructor/certificates"
          component={GuardedInstructorCertificates}
        />
        {/* Issuing by hand is on Enrollments (each student's row). */}
        <Route path="/instructor/certificates/issue">
          <Redirect to="/instructor/enrollments" replace />
        </Route>
        <ProtectedRoute path="/instructor/assessments" component={GuardedInstructorAssessments} />
        <Route path="/instructor/essays" component={EssaysToAssessments} />
        {/* Showed the whole catalogue, not this instructor's courses; their
            class page lists exactly their published programmes. */}
        <Route path="/instructors/:instructorId/courses">
          {(params: { instructorId: string }) => <Redirect to={`/class/${params.instructorId}`} />}
        </Route>
        {/* === PROFESSIONAL PANEL (jobs / profile hub — separate from instructor teaching shell) === */}
        <PanelRoute path="/professional" panelType="professional" component={Pages.ProfessionalDashboard} />
        <PanelRoute path="/professional/dashboard" panelType="professional" component={Pages.ProfessionalDashboard} />
        <PanelRoute path="/professional/profile" panelType="professional" component={Pages.ProfessionalProfileHub} />
        <PanelRoute path="/professional/jobs" panelType="professional" component={Pages.ProfessionalJobs} />
        <PanelRoute path="/professional/settings" panelType="professional" component={Pages.ProfessionalSettings} />

        {/* === INSTRUCTOR PANEL ===
            Every authoring route requires the assigned INSTRUCTOR role,
            matching the Seller Panel below — the unguarded ProtectedRoute
            definitions further up are the ones that let any signed-in user in.
            `/instructor/resources` still redirects: selling resources is the
            Seller Panel's job, and there is no instructor-side equivalent. */}
        <Route path="/instructor/resources" component={() => <Redirect to="/seller/resources" />} />
        <PanelRoute path="/instructor" panelType="instructor" component={Pages.InstructorDashboard} />
        <PanelRoute path="/instructor/dashboard" panelType="instructor" component={Pages.InstructorDashboard} />
        <PanelRoute path="/instructor/enrollments" panelType="instructor" component={Pages.InstructorEnrollments} />
        <PanelRoute path="/instructor/earnings" panelType="instructor" component={Pages.InstructorEarnings} />
        {/* Course sales and Payouts became Earnings. /instructor/payouts is also
            where Stripe returns people (connect.ts), so its query is kept. */}
        <Route path="/instructor/sales" component={() => <EarningsRedirect tab="sales" extra="type=course" />} />
        <Route path="/instructor/payouts" component={() => <EarningsRedirect tab="payouts" />} />
        <PanelRoute path="/instructor/settings" panelType="instructor" component={Pages.InstructorSettings} />
        <PanelRoute panelType="instructor"
          path="/instructor/exams/create"
          component={Pages.ExamBuilder}
        />
        <PanelRoute panelType="instructor"
          path="/instructor/exams/:id/edit"
          component={Pages.ExamBuilder}
        />

        {/* === SELLER PANEL ROUTES (Individual Routes like Instructor) === */}
        <PanelRoute path="/seller" panelType="control" component={Pages.SellerDashboard} />
        <PanelRoute path="/seller/dashboard" panelType="control" component={Pages.SellerDashboard} />
        <PanelRoute path="/seller/resources" panelType="control" component={Pages.SellerResources} />
        {/* Courses are run from Instructor panel -> My Courses (one list, not two). */}
        <Route path="/seller/courses">
          <Redirect to="/instructor/courses" replace />
        </Route>
        <PanelRoute path="/seller/analytics" panelType="control" component={Pages.SellerAnalytics} />
        <PanelRoute path="/seller/storefront" panelType="control" component={Pages.SellerStorefront} />
        <PanelRoute path="/seller/payments" panelType="control" component={Pages.SellerPayments} />
        <PanelRoute path="/seller/settings" panelType="control" component={Pages.SellerSettings} />

        {/* Admin has moved to a separate web app */}
        <Route path="/admin/:rest*">
          {() => {
            if (typeof window !== "undefined") {
              window.location.href = `https://admin.dancerealmx.com${window.location.pathname}${window.location.search}`;
            }
            return null;
          }}
        </Route>
        <Route path="/admin">
          {() => {
            if (typeof window !== "undefined") {
              window.location.href = "https://admin.dancerealmx.com";
            }
            return null;
          }}
        </Route>

        {/* Smart Panel Redirect - automatically routes to correct panel */}
        <Route path="/panel" component={SmartPanelRedirect} />

        {/* Fallback Panel Redirect - for unauthorized access attempts */}
        <Route path="/redirect-panel" component={PanelRedirect} />

        {/* Canonical "Browse Professionals" route — mirrors /jobs.
            Pages.ProfessionalDirectory was the old, half-built directory and
            is now retired in favor of the ConnectSearch component which has
            the full filter/ZIP/radius/sort UI and properly formatted names. */}
        <Route path="/professionals" component={Pages.ConnectSearch} />

        {/* 404 Not Found - Must be last */}
        <Route component={Pages.NotFound} />
      </Switch>
    </Suspense>
  );
}

// Component to handle modals (needs to be inside providers)
function AppWithModals() {
  const { hasPendingPayments } = usePendingPayment();

  return (
    <>
      <EmailVerificationBanner />
      <GooglePasswordSetupBanner />
      <Router />
      <Toaster />
      <SonnerToaster />
      <SlowRequestNotice />
      {hasPendingPayments && (
        <PendingPaymentModal
          isOpen={true}
          onClose={() => { }}
        />
      )}
    </>
  );
}

function App() {
  // Initialize error monitoring system
  useEffect(() => {
    setupGlobalErrorHandling();
    setupStripeErrorHandler();
    console.log("🛡️ Error monitoring system initialized");
    console.log("🔒 Stripe error handler initialized");
  }, []);

  return (
    <ErrorBoundary>
      <CartProvider>
        <ConfirmDialogProvider>
          <AppWithModals />
        </ConfirmDialogProvider>
      </CartProvider>
    </ErrorBoundary>
  );
}

export default App;
